// Helpers shared by the learner provider and the facilitator pages: how a canvas entry row is read and written.
import { DEFS, DEF_BY_NO } from '@/canvas/defs/index';
import { evaluateAll, defaultAnswers } from '@/canvas/engine';
import { has } from '@/canvas/helpers';

export const ENTRY_TABLE = 'canvas_entries_c';

export const ENTRY_FIELDS = [
  'Id',
  'Name',
  'canvas_no_c',
  'status_c',
  'answers_c',
  'completion_c',
  'confirmed_on_c',
  'organisation_c',
  'ModifiedOn',
];

export function entryTitle(def) {
  return def.no === 0 ? 'Case Card' : `Canvas ${def.no}: ${def.title}`;
}

export function parseAnswers(text) {
  try {
    const parsed = JSON.parse(text || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

// one database row -> { no, modifiedOn, entry }
export function parseRow(row) {
  const raw = Number(row.canvas_no_c);
  const no = Number.isInteger(raw) && DEF_BY_NO[raw] ? raw : null;
  const v = parseAnswers(row.answers_c);
  return {
    no,
    modifiedOn: row.ModifiedOn || '',
    entry: {
      rowId: row.Id,
      v,
      status: row.status_c === 'Confirmed' ? 'Confirmed' : undefined,
      confirmedOn: row.confirmed_on_c || v._confirmedOn || null,
      modifiedOn: row.ModifiedOn || null,
      organisation: row.organisation_c || '',
      rev: 0,
      savedRev: 0,
    },
  };
}

// local entries -> what the engine reads
export function toEngine(entries) {
  const out = {};
  Object.keys(entries || {}).forEach((no) => {
    out[no] = { v: entries[no].v, status: entries[no].status };
  });
  return out;
}

// rows of one learner -> entries by canvas number (the latest row wins when a canvas has two)
export function entriesFromRows(rows) {
  const map = {};
  const stamp = {};
  (rows || []).forEach((row) => {
    const p = parseRow(row);
    if (p.no === null) return;
    if (!map[p.no] || String(p.modifiedOn) > String(stamp[p.no])) {
      map[p.no] = p.entry;
      stamp[p.no] = p.modifiedOn;
    }
  });
  return map;
}

// has the learner typed anything beyond the starting values a canvas puts in by itself?
export function typedAnswers(def, v) {
  const base = defaultAnswers(def);
  return Object.keys(v || {}).some((k) => !k.startsWith('_') && !k.endsWith('._n') && has(v[k]) && String(v[k]) !== base[k]);
}

// every canvas evaluated in order; a canvas is only "in progress" once the learner has typed something of their own
export function evaluateEntries(entries) {
  const out = evaluateAll(toEngine(entries), DEFS);
  Object.values(out.states).forEach((st) => {
    st.started = typedAnswers(st.def, st.v);
    if (!st.confirmed) st.status = st.started ? 'In progress' : 'Not started';
    if (st.total === 0) st.completion = st.started ? 100 : 0;
  });
  return out;
}
